import { CATEGORY_RANKING_STAGE_OPTIONS } from '@/api/category-ranking/types';
import type { EloCategory } from '@/components/tournament/models/elo.model';
import type { ManagerGender } from '@/schemas/managerRegisterPairSchema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Gender } from '@/schemas/registerSchema';
import { getCategoryRankingStageLabel } from '@/utils/category-ranking-utils';
import { getGenderLabel } from '@/utils/gender-utils';
import { emptyGridRow, type ImportGridRow } from '@/utils/ranking-import-csv';
import { Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import { memo, useCallback } from 'react';

/** Radix throws on an empty-string SelectItem value, so "no gender yet" needs a sentinel. */
const NO_GENDER = 'NONE';
const GENDER_VALUES = Object.values(Gender);

interface RankingImportGridRowProps {
  row: ImportGridRow;
  index: number;
  categories: EloCategory[];
  onUpdate: (index: number, patch: Partial<ImportGridRow>) => void;
  onRemove: (index: number) => void;
}

/**
 * Memoized because the grid is unvirtualized and holds up to RANKING_IMPORT_MAX_ROWS rows, each
 * mounting six controls (three of them Radix Selects, which carry their own refs and positioning).
 * `onUpdate` rebuilds only the edited row and leaves every other row's reference untouched, so
 * this cuts a keystroke from re-rendering the whole table down to re-rendering one row.
 */
const RankingImportGridRow = memo<RankingImportGridRowProps>(
  ({ row, index, categories, onUpdate, onRemove }) => (
    <tr className={index > 0 ? 'border-t border-gray-100' : ''}>
      <td className="px-2 py-1.5">
        <Input
          className="h-9 w-[200px]"
          placeholder="jugador@mail.com"
          aria-label={`Email de la fila ${index + 1}`}
          value={row.email}
          onChange={(event) => onUpdate(index, { email: event.target.value })}
        />
      </td>
      <td className="px-2 py-1.5">
        <Input
          className="h-9 w-[160px]"
          aria-label={`Nombre de la fila ${index + 1}`}
          value={row.name}
          onChange={(event) => onUpdate(index, { name: event.target.value })}
        />
      </td>
      <td className="px-2 py-1.5">
        <Select
          value={row.gender || NO_GENDER}
          onValueChange={(value) =>
            // Radix hands back a bare string; the items come from Gender, so the
            // non-sentinel branch is always a valid member.
            onUpdate(index, {
              gender: value === NO_GENDER ? '' : (value as ManagerGender),
            })
          }
        >
          <SelectTrigger
            className="h-9 w-[120px]"
            aria-label={`Género de la fila ${index + 1}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_GENDER}>—</SelectItem>
            {GENDER_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {getGenderLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5">
        <Input
          type="date"
          className="h-9 w-[150px]"
          aria-label={`Nacimiento de la fila ${index + 1}`}
          value={row.birthdate}
          onChange={(event) => onUpdate(index, { birthdate: event.target.value })}
        />
      </td>
      <td className="px-2 py-1.5">
        <Select
          value={row.categoryId !== undefined ? String(row.categoryId) : ''}
          onValueChange={(value) => onUpdate(index, { categoryId: Number(value) })}
        >
          <SelectTrigger
            className="h-9 w-[140px]"
            aria-label={`Categoría de la fila ${index + 1}`}
          >
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.id} value={String(category.id)}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5">
        <Select
          value={row.stage ?? ''}
          onValueChange={(value) =>
            onUpdate(index, { stage: value as ImportGridRow['stage'] })
          }
        >
          <SelectTrigger
            className="h-9 w-[140px]"
            aria-label={`Instancia de la fila ${index + 1}`}
          >
            <SelectValue placeholder="Instancia" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_RANKING_STAGE_OPTIONS.map((stage) => (
              <SelectItem key={stage} value={stage}>
                {getCategoryRankingStageLabel(stage)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-2 py-1.5">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Quitar fila ${index + 1}`}
          onClick={() => onRemove(index)}
        >
          <Trash2 size={16} className="text-gray-400" />
        </Button>
      </td>
    </tr>
  ),
);
RankingImportGridRow.displayName = 'RankingImportGridRow';

interface RankingImportRowsGridProps {
  rows: ImportGridRow[];
  /** Takes an updater so the row callbacks stay referentially stable across edits. */
  onChange: (update: React.SetStateAction<ImportGridRow[]>) => void;
  categories: EloCategory[];
  /** Message shown under a row, keyed by row index. Zod issues and dry-run errors share it. */
  rowErrors: Record<number, string>;
}

export const RankingImportRowsGrid: React.FC<RankingImportRowsGridProps> = ({
  rows,
  onChange,
  categories,
  rowErrors,
}) => {
  const updateRow = useCallback(
    (index: number, patch: Partial<ImportGridRow>) => {
      onChange((current) =>
        current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
      );
    },
    [onChange],
  );

  const removeRow = useCallback(
    (index: number) => {
      onChange((current) => {
        const next = current.filter((_, i) => i !== index);
        return next.length > 0 ? next : [emptyGridRow()];
      });
    },
    [onChange],
  );

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Nombre, género y nacimiento solo hacen falta si el jugador todavía no
        tiene cuenta en GetMatch.
      </p>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-2 py-2 text-left font-medium">Email</th>
              <th className="px-2 py-2 text-left font-medium">Nombre</th>
              <th className="px-2 py-2 text-left font-medium">Género</th>
              <th className="px-2 py-2 text-left font-medium">Nacimiento</th>
              <th className="px-2 py-2 text-left font-medium">Categoría</th>
              <th className="px-2 py-2 text-left font-medium">Instancia</th>
              <th className="w-10 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <RankingImportGridRow
                key={row.key}
                row={row}
                index={index}
                categories={categories}
                onUpdate={updateRow}
                onRemove={removeRow}
              />
            ))}
          </tbody>
        </table>
      </div>

      {Object.keys(rowErrors).length > 0 && (
        <ul className="space-y-1 text-xs text-red-600">
          {Object.entries(rowErrors).map(([index, message]) => (
            <li key={index}>
              Fila {Number(index) + 1}: {message}
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange((current) => [...current, emptyGridRow()])}
      >
        <Plus size={16} className="mr-1.5" />
        Agregar fila
      </Button>
    </div>
  );
};
